import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { PersistColumnWidthsDirective } from '../../shared/directives/persist-column-widths.directive';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MultiSelect } from 'primeng/multiselect';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { EffortVarianceReportService } from '../../services/effort-variance-report.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import {
  EffortVarianceReport,
  EffortVarianceEmployeeSummary,
  EffortVarianceRow,
  EffortVarianceStatus,
  EffortVarianceUnallocatedEmployee
} from '../../models/effort-variance-report.model';
import { autoFitColumns, addAutoFilter } from '../../utils/excel-export.util';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-effort-variance-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    PersistColumnWidthsDirective,
    Select,
    Tooltip,
    Tag,
    ToggleSwitch,
    MultiSelect,
    Dialog,
    ButtonModule
  ],
  templateUrl: './effort-variance-report.component.html',
  styleUrl: './effort-variance-report.component.scss'
})
export class EffortVarianceReportComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private reportService = inject(EffortVarianceReportService);
  private yearMonthFilter = inject(YearMonthFilterService);
  private messageService = inject(MessageService);
  public pageHeaderService = inject(PageHeaderSearchActionService);

  report: EffortVarianceReport | null = null;
  loading = false;

  selectedYear: number;
  selectedMonth: number;
  yearOptions: DropdownOption[] = [];
  monthOptions: DropdownOption[] = [];

  /**
   * Draft work logs count towards actuals by default so the report is usable mid-month.
   * Each row still shows its own timesheet status.
   */
  includeDrafts = true;

  searchText = '';
  selectedProjectIds: string[] = [];
  selectedStatuses: EffortVarianceStatus[] = [];

  /**
   * Narrows the table to employees who logged nothing. Kept separate from selectedStatuses
   * because "not logged" is not a status the backend assigns — someone with no work log is
   * still classified Under (or Over, given unplanned work), so it cannot be expressed as a
   * status value without mislabelling those rows.
   */
  showOnlyNotLogged = false;
  statusOptions: { label: string; value: EffortVarianceStatus }[] = [
    { label: 'Over allocated effort', value: 'Over' },
    { label: 'Under allocated effort', value: 'Under' },
    { label: 'On track', value: 'OnTrack' },
    { label: 'Unplanned work', value: 'NoAllocation' },
    { label: 'Not logged', value: 'NoTimesheet' }
  ];

  expandedRows: Record<string, boolean> = {};

  constructor() {
    this.selectedYear = this.yearMonthFilter.currentYear;
    this.selectedMonth = this.yearMonthFilter.currentMonth;
  }

  ngOnInit(): void {
    this.yearOptions = this.yearMonthFilter.getYearOptions();
    this.monthOptions = this.yearMonthFilter.getMonthOptions();

    // Drive search, filter toggle and Export from the shared main header, the same way the
    // other report pages do, so this page has no controls of its own competing with it.
    this.pageHeaderService.clearPageConfig();
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search employee or project...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
      });

    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    this.loadReport();
  }

  ngOnDestroy(): void {
    // Reset so the header's search/export/toggle do not linger on the next page.
    this.pageHeaderService.clearPageConfig();
  }

  loadReport(): void {
    this.loading = true;
    this.report = null;

    this.reportService
      .getReport(this.selectedYear, this.selectedMonth, this.includeDrafts)
      .pipe(
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Could not load report',
            detail: 'Failed to load the effort variance report. Please try again.'
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.report = result;
        this.loading = false;
      });
  }

  onFilterChange(): void {
    this.loadReport();
  }

  // ---------- derived views ----------

  /**
   * Every allocated employee, in one table. An earlier version split people with no work log
   * into a separate panel; that hid them from sorting, filtering and the totals, and made the
   * page read as two different reports. They now appear inline with a "Not logged" status, so
   * the table is the single view of the month and the reader decides what to focus on.
   */
  get allEmployees(): EffortVarianceEmployeeSummary[] {
    return this.sortNotLoggedLast(this.applyFilters(this.report?.employees ?? []));
  }

  /**
   * Keeps employees who logged nothing at the bottom of the default view.
   *
   * The backend orders by largest absolute variance, which puts every not-logged row first:
   * no hours against a full allocation is always the biggest gap arithmetically. That buried
   * genuine over/under effort below a screenful of data-entry gaps. Ordering within each
   * group is left as the backend set it, so the biggest real variances still lead.
   *
   * Only the initial order — clicking a column header hands sorting to the table, which is
   * what someone asking for "worst variance first" expects.
   */
  private sortNotLoggedLast(rows: EffortVarianceEmployeeSummary[]): EffortVarianceEmployeeSummary[] {
    const logged: EffortVarianceEmployeeSummary[] = [];
    const notLogged: EffortVarianceEmployeeSummary[] = [];

    for (const row of rows) {
      (this.isNotLogged(row) ? notLogged : logged).push(row);
    }

    return [...logged, ...notLogged];
  }

  /**
   * True when nothing was logged for the month. Covers both an absent work log and one that
   * exists but has no hours against any project.
   */
  isNotLogged(employee: EffortVarianceEmployeeSummary): boolean {
    return employee.timesheetStatus === 'NotEntered' || employee.actualHours <= 0;
  }

  /**
   * Projects present in the current month's report. Derived from the data rather than the
   * full project list, so the dropdown never offers a project with nothing to show.
   */
  get projectOptions(): { label: string; value: string }[] {
    const byId = new Map<string, string>();

    for (const employee of this.report?.employees ?? []) {
      for (const project of employee.projects) {
        if (project.projectId) {
          byId.set(project.projectId, project.projectName || '(unnamed project)');
        }
      }
    }

    return [...byId.entries()]
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private applyFilters(
    rows: EffortVarianceEmployeeSummary[],
    options: { ignoreStatusFilters?: boolean } = {}
  ): EffortVarianceEmployeeSummary[] {
    const search = this.searchText.trim().toLowerCase();
    const statuses = options.ignoreStatusFilters ? [] : this.selectedStatuses;

    return rows.filter(row => {
      if (!options.ignoreStatusFilters && this.showOnlyNotLogged && !this.isNotLogged(row)) {
        return false;
      }

      if (search) {
        const matchesEmployee = row.employeeName.toLowerCase().includes(search);
        const matchesProject = row.projects.some(p =>
          (p.projectName || '').toLowerCase().includes(search)
        );
        if (!matchesEmployee && !matchesProject) {
          return false;
        }
      }

      if (this.selectedProjectIds.length) {
        // Keep the employee when ANY of their projects is selected — the expanded detail
        // still shows all of their projects, so their total stays meaningful.
        const onSelectedProject = row.projects.some(
          p => p.projectId && this.selectedProjectIds.includes(p.projectId)
        );
        if (!onSelectedProject) {
          return false;
        }
      }

      if (statuses.length) {
        // Match on the employee total OR any project row, so filtering by "Over" still finds
        // someone whose overall total nets out but who overran on a single project.
        const matches =
          statuses.includes(row.status) || row.projects.some(p => statuses.includes(p.status));
        if (!matches) {
          return false;
        }
      }

      return true;
    });
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim()
      || this.selectedProjectIds.length > 0
      || this.selectedStatuses.length > 0
      || this.showOnlyNotLogged;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedProjectIds = [];
    this.selectedStatuses = [];
    this.showOnlyNotLogged = false;
    // Search lives in the shared header, so clear it there too — otherwise the header keeps
    // the text and the table stays filtered.
    this.pageHeaderService.setSearchText('');
  }

  // ---------- card shortcuts ----------

  /**
   * Cards double as filters: clicking one narrows the table to the people it counts, and
   * clicking it again clears that. Toggling rather than only setting means the card is its
   * own "off" switch, so nobody has to find the Clear button to get back.
   */
  toggleStatusFilter(status: EffortVarianceStatus): void {
    const alreadyOnlyThis =
      !this.showOnlyNotLogged &&
      this.selectedStatuses.length === 1 &&
      this.selectedStatuses[0] === status;

    this.showOnlyNotLogged = false;
    this.selectedStatuses = alreadyOnlyThis ? [] : [status];
  }

  /** The not-logged card — its own flag, since it is not a status. See showOnlyNotLogged. */
  toggleNotLoggedFilter(): void {
    const alreadyOnlyThis = this.showOnlyNotLogged && this.selectedStatuses.length === 0;

    this.selectedStatuses = [];
    this.showOnlyNotLogged = !alreadyOnlyThis;
  }

  /**
   * The Variance dropdown and the not-logged card both narrow the same table, so picking a
   * status there releases the card's filter. Leaving both on would read as one filter while
   * two were applied, and could show an empty table with no obvious cause.
   */
  onStatusFilterChange(): void {
    if (this.selectedStatuses.length > 0) {
      this.showOnlyNotLogged = false;
    }
  }

  /** True when the table is currently narrowed to exactly this status, for the active style. */
  isStatusFilterActive(status: EffortVarianceStatus): boolean {
    return !this.showOnlyNotLogged
      && this.selectedStatuses.length === 1
      && this.selectedStatuses[0] === status;
  }

  get isNotLoggedFilterActive(): boolean {
    return this.showOnlyNotLogged && this.selectedStatuses.length === 0;
  }

  // ---------- totals ----------

  // Totals cover EVERY allocated employee currently in view, including those who logged
  // nothing. Expected is the month's planned capacity, so leaving out people with no work log
  // understated it and made Expected vs Actual an unfair comparison.

  get totalExpectedHours(): number {
    return this.allEmployees.reduce((sum, e) => sum + (e.expectedHours || 0), 0);
  }

  get totalActualHours(): number {
    return this.allEmployees.reduce((sum, e) => sum + (e.actualHours || 0), 0);
  }

  get totalVarianceHours(): number {
    return this.totalActualHours - this.totalExpectedHours;
  }

  /**
   * The population the status cards count: search and project filters applied, but NOT the
   * status filter the cards themselves set. If the cards counted their own output, clicking
   * "over" would zero the other three and leave no way back except Clear.
   */
  private get statusCountBase(): EffortVarianceEmployeeSummary[] {
    return this.applyFilters(this.report?.employees ?? [], { ignoreStatusFilters: true });
  }

  get overCount(): number {
    return this.statusCountBase.filter(e => e.status === 'Over' && !this.isNotLogged(e)).length;
  }

  /**
   * Under-delivery that was actually logged. People who logged nothing are reported by
   * notLoggedInViewCount instead: a missing work log is a data-entry gap, not evidence of under
   * effort, so folding them in here would overstate under-delivery. They are counted
   * somewhere, though — every employee in view falls into exactly one of onTrack / over /
   * under / notLogged, so the cards account for the whole table.
   */
  get underCount(): number {
    return this.statusCountBase.filter(e => e.status === 'Under' && !this.isNotLogged(e)).length;
  }

  /** Employees in view who logged nothing — shown on the card so no one is silently dropped. */
  get notLoggedInViewCount(): number {
    return this.statusCountBase.filter(e => this.isNotLogged(e)).length;
  }

  get onTrackCount(): number {
    return this.statusCountBase.filter(e => e.status === 'OnTrack' && !this.isNotLogged(e)).length;
  }

  /** Rows currently in the table. The table scrolls rather than paginating, so without this
   *  there is no way to know how many people the figures above cover. */
  get shownEmployeeCount(): number {
    return this.allEmployees.length;
  }

  /** Everyone in the month's report, before any filter — the denominator for "12 of 90". */
  get totalEmployeeCount(): number {
    return this.report?.employees?.length ?? 0;
  }

  /** Allocatable this month but with nothing planned — listed below the variance table. */
  get unallocatedEmployees(): EffortVarianceUnallocatedEmployee[] {
    const rows = this.report?.unallocatedEmployees ?? [];
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      return rows;
    }
    return rows.filter(e => e.employeeName.toLowerCase().includes(search));
  }

  get unallocatedCount(): number {
    return this.unallocatedEmployees.length;
  }

  /**
   * Shown in a dialog rather than inline. An inline panel put the whole of a quiet month —
   * October 2026 is 78 people with no plan — behind a chevron at the foot of an empty table.
   * A dialog gives the list the room to be read, and matches the workforce popup on the
   * Monthly Allocation page, which answers the same question from the planning side.
   */
  showUnallocatedDialog = false;

  openUnallocatedDialog(): void {
    this.showUnallocatedDialog = true;
  }

  closeUnallocatedDialog(): void {
    this.showUnallocatedDialog = false;
  }

  // ---------- display helpers ----------

  /** Formats decimal hours as "Xh Ymin" — matches the work log's own presentation. */
  formatHours(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const sign = decimalHours < 0 ? '-' : '';
    const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
    return `${sign}${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
  }

  /**
   * Aggregate hours for the KPI cards. At company scale the minutes are noise — nobody reads
   * the 7 minutes in "9744h 7min" — and the digits run together without a separator. Rounds
   * to whole hours and groups thousands. Per-row values keep their minutes via formatHours.
   */
  formatHoursCompact(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const rounded = Math.round(decimalHours);
    return `${rounded.toLocaleString('en-US')} h`;
  }

  /** Aggregate variance, signed, so over vs under is readable at a glance. */
  formatVarianceCompact(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const rounded = Math.round(decimalHours);
    const prefix = rounded > 0 ? '+' : '';
    return `${prefix}${rounded.toLocaleString('en-US')} h`;
  }

  /** Variance with an explicit sign, so "more than planned" reads unambiguously. */
  formatVariance(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const prefix = decimalHours > 0 ? '+' : '';
    return `${prefix}${this.formatHours(decimalHours)}`;
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(1)}%`;
  }

  formatDays(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  statusLabel(status: EffortVarianceStatus): string {
    switch (status) {
      case 'OnTrack': return 'On track';
      case 'Over': return 'Over';
      case 'Under': return 'Under';
      case 'NoAllocation': return 'Unplanned';
      case 'NoTimesheet': return 'Not logged';
      default: return status;
    }
  }

  /** Over and Under are both amber/red rather than "bad vs good" — neither is inherently wrong. */
  statusSeverity(status: EffortVarianceStatus): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'OnTrack': return 'success';
      case 'Over': return 'danger';
      case 'Under': return 'warn';
      case 'NoAllocation': return 'info';
      case 'NoTimesheet': return 'secondary';
      default: return 'secondary';
    }
  }

  /** "NotEntered" is not a phrase anyone says — show it as "Not entered". */
  timesheetStatusLabel(status: string): string {
    return status === 'NotEntered' ? 'Not entered' : status;
  }

  timesheetStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'Approved': return 'success';
      case 'Submitted': return 'info';
      case 'Draft': return 'warn';
      case 'NotEntered': return 'danger';
      default: return 'secondary';
    }
  }

  /** True when the variance is large enough to deserve visual emphasis. */
  isSignificantVariance(row: EffortVarianceRow | EffortVarianceEmployeeSummary): boolean {
    const tolerance = this.report?.onTrackTolerancePercentage ?? 10;
    const pct = row.variancePercentage;
    return pct != null && Math.abs(pct) > tolerance * 2.5;
  }

  toggleRow(employeeId: string): void {
    this.expandedRows[employeeId] = !this.expandedRows[employeeId];
  }

  isExpanded(employeeId: string): boolean {
    return !!this.expandedRows[employeeId];
  }

  get monthLabel(): string {
    return this.monthOptions.find(m => m.value === this.selectedMonth)?.label ?? '';
  }

  // ---------- export ----------

  exportToExcel(): void {
    if (!this.report || !this.allEmployees.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'There is no report data for the selected month.'
      });
      return;
    }

    try {
      // One row per employee/project, matching what is on screen. The employee total is
      // included on its own row so the export stands alone without the expand interaction.
      const rows: Record<string, unknown>[] = [];

      for (const employee of this.allEmployees) {
        rows.push({
          'Employee': employee.employeeName,
          'Project': '— ALL PROJECTS (total) —',
          'Allocation %': employee.totalAllocationPercentage,
          'Working Days': employee.workingDays,
          'Leave Days': employee.leaveDays,
          'Available Days': employee.availableDays,
          'Expected Hours': this.round2(employee.expectedHours),
          'Actual Hours': this.round2(employee.actualHours),
          'Variance Hours': this.round2(employee.varianceHours),
          'Variance %': employee.variancePercentage ?? '',
          'Status': this.statusLabel(employee.status),
          'Work Log Status': employee.timesheetStatus
        });

        for (const project of employee.projects) {
          rows.push({
            'Employee': employee.employeeName,
            'Project': project.projectName,
            'Allocation %': project.allocationPercentage,
            'Working Days': project.workingDays,
            'Leave Days': project.leaveDays,
            'Available Days': project.availableDays,
            'Expected Hours': this.round2(project.expectedHours),
            'Actual Hours': this.round2(project.actualHours),
            'Variance Hours': this.round2(project.varianceHours),
            'Variance %': project.variancePercentage ?? '',
            'Status': this.statusLabel(project.status),
            'Work Log Status': project.timesheetStatus
          });
        }
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      autoFitColumns(worksheet, rows);
      addAutoFilter(worksheet);

      const sheets: Record<string, XLSX.WorkSheet> = { 'Effort Variance': worksheet };
      const sheetNames = ['Effort Variance'];

      // Second sheet rather than extra rows on the first: these people have no hours, so
      // they would need every numeric column left blank and would break any total or filter
      // applied to the variance sheet.
      const unallocated = this.unallocatedEmployees;
      if (unallocated.length) {
        const unallocatedRows = unallocated.map(e => ({
          'Employee': e.employeeName,
          'Role': e.roleTitle ?? '',
          'Designation': e.designationName ?? '',
          'Service Group': e.serviceGroupName ?? ''
        }));
        const unallocatedSheet = XLSX.utils.json_to_sheet(unallocatedRows);
        autoFitColumns(unallocatedSheet, unallocatedRows);
        addAutoFilter(unallocatedSheet);
        sheets['Not Allocated'] = unallocatedSheet;
        sheetNames.push('Not Allocated');
      }

      const workbook: XLSX.WorkBook = { Sheets: sheets, SheetNames: sheetNames };
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const stamp = new Date().toISOString().slice(0, 10);
      saveAs(blob, `Effort_Variance_${this.monthLabel}_${this.selectedYear}_${stamp}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Exported',
        detail: 'The effort variance report has been downloaded.'
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Export failed',
        detail: 'Could not generate the Excel file.'
      });
    }
  }

  private round2(value: number | null | undefined): number {
    if (value == null || Number.isNaN(value)) {
      return 0;
    }
    return Math.round(value * 100) / 100;
  }
}
